import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import Cookies from "js-cookie"

import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import appendSearchParams from "@utils/appendSearchParams"
import getApiErrorMessages from "@utils/getApiErrorMessages"
import replaceSlugs from "@utils/replaceSlugs"

import toast from "@molecules/Toast/Toast"

import { CATALOG_ENDPOINTS } from "@endpoints/catalogs"

import { SubscribePayload } from "./SettingsCatalogs.types"

async function fetchCatalogJSON<ResponseData>(endpoint: string): Promise<ResponseData> {
    const response = await fetch(endpoint)

    if (!response.ok) {
        throw new Error(`Request to ${endpoint} failed with status ${response.status}.`)
    }

    return (await response.json()) as ResponseData
}

export function useCatalogsQuery() {
    const { user } = useUser()
    const [catalogsEndpoint] = useSlugExtractor([CATALOG_ENDPOINTS.CATALOGS])

    return useQuery({
        queryKey: ["catalogs", catalogsEndpoint],
        queryFn: () => fetchCatalogJSON<Catalog[]>(catalogsEndpoint),
        enabled: !!user?.service_company?.slug,
    })
}

export function useCatalogBrandsQuery(catalogId: number) {
    const { user } = useUser()
    const [brandsEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.BRANDS])
    const brandsEndpoint = appendSearchParams(brandsEndpointTemplate, { catalog: catalogId })

    return useQuery({
        queryKey: ["catalog-brands", catalogId, brandsEndpoint],
        queryFn: () => fetchCatalogJSON<CatalogBrand[]>(brandsEndpoint),
        enabled: !!user?.service_company?.slug,
    })
}

export function useBrandCountsQuery(catalogId: number) {
    const { user } = useUser()
    const [brandCountsEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.BRAND_COUNTS])
    const brandCountsEndpoint = appendSearchParams(brandCountsEndpointTemplate, { catalog: catalogId })

    return useQuery({
        queryKey: ["catalog-brand-counts", catalogId, brandCountsEndpoint],
        queryFn: () => fetchCatalogJSON<CatalogBrandCount[]>(brandCountsEndpoint),
        enabled: !!user?.service_company?.slug,
    })
}

export function useCatalogSubscriptionsQuery(catalogId: number) {
    const { user } = useUser()
    const [subscriptionsEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTIONS])
    const subscriptionsEndpoint = appendSearchParams(subscriptionsEndpointTemplate, { catalog: catalogId })

    return useQuery({
        queryKey: ["catalog-subscriptions", catalogId, subscriptionsEndpoint],
        queryFn: () => fetchCatalogJSON<CatalogSubscription[]>(subscriptionsEndpoint),
        enabled: !!user?.service_company?.slug,
    })
}

// A subscription change never alters brand identities, only their derived coverage — so a subscribe or
// unsubscribe invalidates just the affected catalog's subscriptions listing and the per-brand counts that
// read off it, leaving the brand cache untouched.
function useInvalidateSubscriptionState() {
    const queryClient = useQueryClient()

    return (catalogId: number) => {
        void queryClient.invalidateQueries({ queryKey: ["catalog-subscriptions", catalogId] })
        void queryClient.invalidateQueries({ queryKey: ["catalog-brand-counts", catalogId] })
    }
}

export function useSubscriptionPreviewCountMutation() {
    const [previewCountEndpoint] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTION_PREVIEW_COUNT])

    return useMutation<CatalogSubscriptionPreviewCount, ApiErrorResponse, SubscribePayload>({
        mutationFn: async (payload) => {
            const response = await fetch(previewCountEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                },
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                throw (await response.json()) as ApiErrorResponse
            }

            return (await response.json()) as CatalogSubscriptionPreviewCount
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not preview the subscription." })
        },
    })
}

export function useSubscribeMutation() {
    const [subscriptionsEndpoint] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTIONS])
    const invalidateSubscriptionState = useInvalidateSubscriptionState()

    return useMutation<CatalogSubscribeResponse, ApiErrorResponse, SubscribePayload>({
        mutationFn: async (payload) => {
            const response = await fetch(subscriptionsEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                },
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                throw (await response.json()) as ApiErrorResponse
            }

            return (await response.json()) as CatalogSubscribeResponse
        },
        onSuccess: (subscription) => {
            invalidateSubscriptionState(subscription.catalog)

            if (subscription.created) {
                toast({ type: "success", title: `Subscribed to ${subscription.scope_label}` })
            } else {
                toast({ type: "info", title: `Already covered by your ${subscription.scope_label} subscription` })
            }
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not subscribe." })
        },
    })
}

export function useUnsubscribeMutation() {
    const [subscriptionDetailsEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTION_DETAILS])
    const invalidateSubscriptionState = useInvalidateSubscriptionState()

    return useMutation<void, ApiErrorResponse, CatalogSubscription>({
        mutationFn: async (subscription) => {
            const response = await fetch(
                replaceSlugs(subscriptionDetailsEndpointTemplate, { id: String(subscription.id) }),
                {
                    method: "DELETE",
                    headers: {
                        Accept: "application/json",
                        "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                    },
                },
            )

            if (!response.ok) {
                throw (await response.json()) as ApiErrorResponse
            }
        },
        onSuccess: (data, subscription) => {
            invalidateSubscriptionState(subscription.catalog)
            toast({ type: "success", title: `Unsubscribed from ${subscription.scope_label}` })
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not delete the subscription." })
        },
    })
}

export function useCatalogResyncMutation() {
    const queryClient = useQueryClient()
    const [resyncEndpointTemplate, itemsEndpoint] = useSlugExtractor([
        CATALOG_ENDPOINTS.CATALOG_RESYNC,
        CATALOG_ENDPOINTS.ITEMS,
    ])

    return useMutation<CatalogResyncResponse, ApiErrorResponse, Catalog>({
        mutationFn: async (catalog) => {
            const response = await fetch(replaceSlugs(resyncEndpointTemplate, { id: String(catalog.id) }), {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                },
            })

            if (!response.ok) {
                throw (await response.json()) as ApiErrorResponse
            }

            return (await response.json()) as CatalogResyncResponse
        },
        onSuccess: (resyncResult, catalog) => {
            // A sync can add or remove brands, categories, and items, so every cache shaped by this
            // catalog is stale; the subscriptions listing is untouched by a resync and left alone.
            void queryClient.invalidateQueries({ queryKey: ["catalogs"] })
            void queryClient.invalidateQueries({ queryKey: ["catalog-brands", catalog.id] })
            void queryClient.invalidateQueries({ queryKey: ["catalog-brand-counts", catalog.id] })
            void queryClient.invalidateQueries({ queryKey: ["catalog-categories", catalog.id] })
            // Item tables key on their list endpoint rather than a catalog id, so they are matched by endpoint.
            void queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === "tableData" && query.queryKey[2] === itemsEndpoint,
            })

            if (resyncResult.result === "started") {
                toast({ type: "success", title: `Syncing ${catalog.name}` })
            } else {
                toast({ type: "info", title: resyncResult.label })
            }
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not start the sync." })
        },
    })
}
